// Libs
import { useCallback, useState } from "react";

// Utils
import type { TFunction } from "src/design-system/core/TranslationContext";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";

// Types
import { ThirdServiceCategory } from "src/types/posCredential";

const SERVICE_RANKING_DEFAULT_VALUE: UsePlatformFilterValue = ThirdServiceCategory.Deliveroo;

export type UsePlatformFilterValue = ThirdServiceCategory;

interface UsePlatformFilterOptions {
    defaultValue?: UsePlatformFilterValue;
    initialValue: UsePlatformFilterValue;
}

export interface UsePlatformFilterResult {
    service: ThirdServiceCategory;
    handleServiceChange: (value: ThirdServiceCategory) => void;
    resetFilter: () => void;
}

const COMPATIBLE_SERVICES = [ThirdServiceCategory.Deliveroo, ThirdServiceCategory.Ubereats];
const INCOMPATIBLE_SERVICES = [ThirdServiceCategory.JustEat, ThirdServiceCategory.Glovo];

// permet d'order les services par compatibilité et donc mettre par exemple Just eat et glovo en dernier dans la liste car le classement n'est pas disponible
// passer le service de INCOMPATIBLE_SERVICES a COMPATIBLE_SERVICES pour le changer ca
export function orderServiceByCompatibility(
    services: ThirdServiceCategory[]
): ThirdServiceCategory[] {
    const compatibleServiceList = [];
    const incompatibleServiceList = [];

    services.forEach((service) => {
        if (COMPATIBLE_SERVICES.includes(service)) {
            compatibleServiceList.push(service);
        } else if (INCOMPATIBLE_SERVICES.includes(service)) {
            incompatibleServiceList.push(service);
        }
    });

    return [...compatibleServiceList, ...incompatibleServiceList];
}

export function getDefaultService(services: ThirdServiceCategory[]): ThirdServiceCategory {
    if (services.length === 0) {
        return undefined;
    }

    // On veut UE par défaut en priorité
    if (services.includes(ThirdServiceCategory.Ubereats)) {
        return ThirdServiceCategory.Ubereats;
    }

    return services[0];
}

/**
 * Hook to handle a Services filter
 * @param options Hooks options
 * @returns Helpers to handle Services filter in Ranking View
 */
export function usePlatformFilter({
    defaultValue,
    initialValue,
}: UsePlatformFilterOptions): UsePlatformFilterResult {
    // Utilisation des services du user ?
    const [service, setPlatform] = useState<ThirdServiceCategory>(initialValue ?? defaultValue);

    const resetFilter = useCallback(() => setPlatform(defaultValue), [setPlatform, defaultValue]);

    return { service, resetFilter, handleServiceChange: setPlatform };
}

/**
 * Format Service filter value
 * @param serviceIds Filter value
 * @param serviceOptions Service options
 * @returns Formatted filter value
 */
export function formatServiceRankingFilter(
    service: UsePlatformFilterValue = SERVICE_RANKING_DEFAULT_VALUE,
    translateRaw: TFunction
): string {
    return translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:service.${service}`);
}
